// app/blog/page.tsx
"use client";

import { motion, AnimatePresence } from "motion/react";
import BlogPostCard from "@/components/Blog/BlogPostCard";
import { useEffect, useState, useMemo } from "react";
import { collection, DocumentData, getDocs } from "firebase/firestore";
import { db } from "@/firebase/firebase";
import { toast } from "sonner";
import BlogPageSkeleton from "./BlogPageSkeleton";
import { Button } from "@heroui/react";
import { HugeiconsIcon } from "@hugeicons/react";
import { Search01Icon, Cancel01Icon, Clock01Icon, ArrowRight01Icon } from "@hugeicons/core-free-icons";
import Image from "next/image";
import Link from "next/link";

// Categories definition
const CATEGORIES = ["All", "Web Development", "Mobile Development", "Software Development", "Frontend", "Backend", "DevOps & Cloud", "AI & ML", "Database", "Design", "Engineering", "Tutorial", "News", "Insight"];

// Helper to determine category for filter grouping
function getCategory(post: DocumentData) {
  if (post?.category) return post.category;
  const titleLower = (post?.title || "").toLowerCase();
  
  if (titleLower.includes("devops") || titleLower.includes("cloud") || titleLower.includes("aws") || titleLower.includes("docker") || titleLower.includes("kubernetes") || titleLower.includes("ci/cd") || titleLower.includes("deployment")) {
    return "DevOps & Cloud";
  }
  if (titleLower.includes("ai") || titleLower.includes("ml") || titleLower.includes("artificial intelligence") || titleLower.includes("machine learning") || titleLower.includes("llm") || titleLower.includes("openai") || titleLower.includes("gpt")) {
    return "AI & ML";
  }
  if (titleLower.includes("database") || titleLower.includes("sql") || titleLower.includes("postgres") || titleLower.includes("mongodb") || titleLower.includes("nosql") || titleLower.includes("redis") || titleLower.includes("firestore") || titleLower.includes("firebase")) {
    return "Database";
  }
  if (titleLower.includes("frontend") || titleLower.includes("tailwind") || titleLower.includes("javascript") || titleLower.includes("typescript") || titleLower.includes("react") || titleLower.includes("next")) {
    return "Frontend";
  }
  if (titleLower.includes("backend") || titleLower.includes("api") || titleLower.includes("node") || titleLower.includes("express") || titleLower.includes("server") || titleLower.includes("rust") || titleLower.includes("go")) {
    return "Backend";
  }
  if (titleLower.includes("web") || titleLower.includes("website") || titleLower.includes("html") || titleLower.includes("css") || titleLower.includes("fullstack")) {
    return "Web Development";
  }
  if (titleLower.includes("mobile") || titleLower.includes("app") || titleLower.includes("ios") || titleLower.includes("android") || titleLower.includes("flutter") || titleLower.includes("react native") || titleLower.includes("swift")) {
    return "Mobile Development";
  }
  if (titleLower.includes("software") || titleLower.includes("system") || titleLower.includes("architecture") || titleLower.includes("pattern") || titleLower.includes("algorithm")) {
    return "Software Development";
  }
  if (titleLower.includes("design") || titleLower.includes("ui") || titleLower.includes("ux") || titleLower.includes("figma")) {
    return "Design";
  }
  if (titleLower.includes("tutorial") || titleLower.includes("guide") || titleLower.includes("how to") || titleLower.includes("build")) {
    return "Tutorial";
  }
  if (titleLower.includes("engineering") || titleLower.includes("code") || titleLower.includes("tech")) {
    return "Engineering";
  }
  if (titleLower.includes("news") || titleLower.includes("announcing") || titleLower.includes("update") || titleLower.includes("launch")) {
    return "News";
  }
  return "Insight";
}

// Helper to get excerpt and read time for Featured Card
function getReadingTimeAndExcerpt(content: string, length = 180) {
  if (!content) {
    return {
      readingTime: "2 min read",
      excerpt: "Click to read this article and explore the full content."
    };
  }
  const plainText = content.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ");
  const words = plainText.trim().split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.ceil(words / 225));
  
  let excerpt = plainText;
  if (plainText.length > length) {
    excerpt = plainText.substring(0, length).trim() + "...";
  }
  return {
    readingTime: `${minutes} min read`,
    excerpt: excerpt || "Click to read this article and explore the full content."
  };
}

export default function BlogPage() {
  const [blogPosts, setBlogPosts] = useState<DocumentData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("All");

  useEffect(() => {
    getEventData();
  }, []);

  const getEventData = async () => {
    setBlogPosts([]);
    try {
      const ref = `/blog/`;
      const eventCollection = collection(db, ref);
      const docSnap = await getDocs(eventCollection);
      const posts: DocumentData[] = [];
      docSnap.forEach((doc) => {
        posts.push(doc.data());
      });

      // Sort posts by createdAt descending (newest first)
      posts.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return dateB.getTime() - dateA.getTime();
      });

      setBlogPosts(posts);
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error("An unknown error occurred");
      }
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  // Filter posts based on category and search query
  const filteredPosts = useMemo(() => {
    return blogPosts.filter((post) => {
      // Category filter
      const categoryMatches = selectedCategory === "All" || getCategory(post).toLowerCase() === selectedCategory.toLowerCase();
      
      // Search query filter
      const searchMatches = !searchQuery.trim() || 
        post.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.author?.toLowerCase().includes(searchQuery.toLowerCase());
      
      return categoryMatches && searchMatches;
    });
  }, [blogPosts, selectedCategory, searchQuery]);

  // Is featured section active? (Only when no search query and category is "All")
  const showFeaturedSection = !searchQuery.trim() && selectedCategory === "All" && filteredPosts.length > 0;
  
  const featuredPost = showFeaturedSection ? filteredPosts[0] : null;
  const gridPosts = showFeaturedSection ? filteredPosts.slice(1) : filteredPosts;

  const featuredInfo = featuredPost ? getReadingTimeAndExcerpt(featuredPost.content, 220) : null;
  const featuredDate = featuredPost?.createdAt
    ? (typeof featuredPost.createdAt.toDate === "function"
      ? featuredPost.createdAt.toDate().toLocaleDateString(undefined, {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : new Date(featuredPost.createdAt).toLocaleDateString(undefined, {
          year: "numeric",
          month: "long",
          day: "numeric",
        }))
    : "";

  const categoryColor: Record<string, string> = {
    "Web Development": "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20",
    "Mobile Development": "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20",
    "Software Development": "bg-teal-500/10 text-teal-600 dark:text-teal-400 border-teal-500/20",
    Frontend: "bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/20",
    Backend: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
    "DevOps & Cloud": "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20",
    "AI & ML": "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20",
    Database: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
    Design: "bg-pink-500/10 text-pink-600 dark:text-pink-400 border-pink-500/20",
    Engineering: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
    Tutorial: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
    News: "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20",
    Insight: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20"
  };

  const featuredCategory = featuredPost ? getCategory(featuredPost) : "Insight";
  const featuredCategoryStyle = categoryColor[featuredCategory] || categoryColor.Insight;

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <BlogPageSkeleton />
      </div>
    );
  }

  return (
    <div className="min-h-screen text-gray-900 dark:text-zinc-100 p-4 sm:p-6 lg:p-8 relative overflow-hidden">
      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* Header / Hero Section (Editorial Layout) */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12 sm:mb-16">
          <motion.div
            className="max-w-2xl text-left"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="text-xs font-bold font-outfit uppercase tracking-widest text-primary mb-3 block">
              Devebsy Journal
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-recoleta font-extrabold tracking-tight text-neutral-900 dark:text-white mb-4 leading-tight">
              Stories & Insights
            </h1>
            <p className="text-base sm:text-lg text-neutral-500 dark:text-zinc-400 font-outfit leading-relaxed">
              Explore deep dives, tech tutorials, and design principles curated by the devebsy team. 
              Discovering {blogPosts.length} articles written to share our craft.
            </p>
          </motion.div>

          {/* Search Bar Container */}
          <motion.div
            className="w-full lg:max-w-sm shrink-0"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
          >
            <div className="relative flex items-center w-full">
              <span className="absolute left-4 text-neutral-400 dark:text-zinc-500">
                <HugeiconsIcon icon={Search01Icon} className="w-4 h-4" size={16} />
              </span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by title or author..."
                className="w-full bg-neutral-100/50 dark:bg-zinc-900/20 border border-neutral-200/60 dark:border-zinc-800/50 focus:border-blue-500/50 dark:focus:border-blue-500/40 rounded-full pl-11 pr-10 py-2.5 text-sm font-outfit focus:outline-none focus:ring-4 focus:ring-blue-500/5 dark:focus:ring-blue-500/5 transition-all text-neutral-800 dark:text-neutral-150 placeholder:text-neutral-400 dark:placeholder:text-zinc-500"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-4 flex items-center justify-center p-0.5 rounded-full text-neutral-400 hover:text-neutral-600 dark:text-zinc-500 dark:hover:text-zinc-300 transition-colors"
                  aria-label="Clear search"
                >
                  <HugeiconsIcon icon={Cancel01Icon} className="w-3.5 h-3.5" size={14} />
                </button>
              )}
            </div>
          </motion.div>
        </div>

        {/* Filter Navigation Tabs */}
        <motion.div 
          className="flex items-center gap-1.5 mb-10 overflow-x-auto pb-3 scrollbar-hidden border-b border-neutral-200/50 dark:border-zinc-800/40 -mx-4 px-4 sm:mx-0 sm:px-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {CATEGORIES.map((category) => {
            const isActive = selectedCategory === category;
            return (
              <button
                key={category}
                onClick={() => {
                  setSelectedCategory(category);
                }}
                className={`relative px-4.5 py-2 text-xs font-semibold rounded-full transition-colors font-outfit shrink-0 focus-visible:outline-none cursor-pointer ${
                  isActive
                    ? "text-neutral-900 dark:text-zinc-900"
                    : "text-neutral-500 hover:text-neutral-950 dark:text-zinc-400 dark:hover:text-zinc-200"
                }`}
              >
                {isActive && (
                  <motion.div
                    layoutId="activeCategory"
                    className="absolute inset-0 bg-neutral-200 dark:bg-white rounded-full z-0"
                    transition={{ type: "spring", stiffness: 350, damping: 25 }}
                  />
                )}
                <span className="relative z-10">{category}</span>
              </button>
            );
          })}
        </motion.div>

        {/* Featured Post Card (Only shown in 'All' and when search is empty) */}
        {showFeaturedSection && featuredPost && featuredInfo && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="group block mb-12"
          >
            <Link href={`/blog/${featuredPost.id}`}>
              <div className="w-full border border-neutral-200/60 dark:border-zinc-850 bg-white/40 dark:bg-zinc-950/30 backdrop-blur-md hover:border-blue-500/40 dark:hover:border-blue-500/35 rounded-[32px] p-4 lg:p-6 shadow-sm hover:shadow-[0_12px_30px_rgba(0,0,0,0.03)] dark:hover:shadow-[0_15px_45px_rgba(0,0,0,0.45)] transition-all duration-500">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-center">
                  
                  {/* Image Block */}
                  <div className="lg:col-span-7 h-64 sm:h-80 lg:h-96 w-full relative overflow-hidden rounded-[22px] bg-neutral-100 dark:bg-zinc-900">
                    {featuredPost.poster ? (
                      <Image
                        src={featuredPost.poster}
                        alt={`Featured cover: ${featuredPost.title}`}
                        fill
                        priority
                        className="object-cover transition-transform duration-700 ease-out group-hover:scale-103"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-neutral-400 dark:text-zinc-600 bg-neutral-100 dark:bg-zinc-900">
                        No Featured Image
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>

                  {/* Content Details Block */}
                  <div className="lg:col-span-5 flex flex-col justify-between py-2 h-full gap-4">
                    <div className="space-y-4">
                      {/* Badge / Category */}
                      <div>
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full border backdrop-blur-md ${featuredCategoryStyle}`}>
                          Featured &bull; {featuredCategory}
                        </span>
                      </div>

                      {/* Title */}
                      <h2 className="text-2xl sm:text-3xl font-recoleta font-bold tracking-tight text-neutral-850 dark:text-neutral-100 group-hover:text-primary leading-tight transition-colors duration-300">
                        {featuredPost.title}
                      </h2>

                      {/* Excerpt */}
                      <p className="text-sm sm:text-base font-outfit text-neutral-500 dark:text-zinc-400 leading-relaxed line-clamp-3 lg:line-clamp-4">
                        {featuredInfo.excerpt}
                      </p>
                    </div>

                    {/* Metadata & Trigger */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-4 border-t border-neutral-150/40 dark:border-zinc-800/40 mt-auto">
                      
                      {/* Author Info */}
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-500/10 dark:bg-blue-500/20 flex items-center justify-center text-xs font-bold text-primary font-outfit border border-blue-500/20">
                          {featuredPost.author ? featuredPost.author.substring(0, 2).toUpperCase() : "AN"}
                        </div>
                        <div>
                          <p className="text-xs font-bold text-neutral-700 dark:text-zinc-350 leading-none">
                            {featuredPost.author || "Anonymous"}
                          </p>
                          <div className="flex items-center gap-2 mt-1 text-[11px] text-neutral-500 dark:text-zinc-500 font-outfit">
                            <div className="flex items-center gap-1">
                              <HugeiconsIcon icon={Clock01Icon} className="w-3 h-3" size={12} />
                              <span>{featuredInfo.readingTime}</span>
                            </div>
                            <span>&bull;</span>
                            <span>{featuredDate}</span>
                          </div>
                        </div>
                      </div>

                      {/* Action */}
                      <div className="flex items-center gap-1.5 text-sm font-semibold text-primary font-outfit self-end sm:self-center">
                        <span>Read Featured Post</span>
                        <HugeiconsIcon 
                          icon={ArrowRight01Icon} 
                          className="w-4 h-4 text-primary group-hover:translate-x-1 transition-transform" 
                          size={16} 
                        />
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </Link>
          </motion.div>
        )}

        {/* Blog Posts Grid / No Articles State */}
        {filteredPosts.length > 0 ? (
          <div className="space-y-6">
            {showFeaturedSection && (
              <h3 className="text-lg font-recoleta font-bold text-neutral-800 dark:text-neutral-150 mb-2">
                Recent Publications
              </h3>
            )}
            
            <motion.div
              layout
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              initial="initial"
              animate="animate"
            >
              <AnimatePresence mode="popLayout">
                {gridPosts.map((post) => (
                  <BlogPostCard key={post.id} post={post} />
                ))}
              </AnimatePresence>
            </motion.div>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-24 border border-dashed border-neutral-200 dark:border-zinc-800 rounded-[32px] bg-white/10 dark:bg-zinc-900/10 backdrop-blur-sm"
          >
            <div className="w-16 h-16 mx-auto rounded-full bg-neutral-100 dark:bg-zinc-800/50 flex items-center justify-center text-neutral-400 dark:text-zinc-500 mb-4">
              <HugeiconsIcon icon={Search01Icon} className="w-6 h-6" size={24} />
            </div>
            <p className="text-xl font-recoleta font-bold text-neutral-800 dark:text-neutral-200">
              No Articles Found
            </p>
            <p className="text-sm font-outfit text-neutral-500 dark:text-zinc-500 mt-2 max-w-sm mx-auto">
              We couldn't find any articles matching "{searchQuery}" in the "{selectedCategory}" category.
            </p>
            <Button
              className="mt-6 font-semibold font-outfit rounded-full px-6 bg-neutral-900 dark:bg-white text-white dark:text-black hover:opacity-90"
              size="md"
              onClick={() => {
                setSearchQuery("");
                setSelectedCategory("All");
              }}
            >
              Reset Search & Filters
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  );
}


